import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import DOMPurify from "dompurify";
import * as Icons from "lucide-react";
import { FileText, MessageCircle, Loader2, ArrowRight, Play, Volume2, Send, X, ChevronDown, Check } from "lucide-react";
import NotFound from "@/pages/NotFound";
import PlacesLogo from "@/components/places/PlacesLogo";
import ChatBubble from "@/components/places/ChatBubble";
import { useContent } from "@/lib/content";

/**
 * LandingPage — renderer público de LPs criadas via CMS.
 *
 * Sob a hood:
 *   - GET /api/lp/:slug — retorna doc completo (só se published=true).
 *   - Cada tipo de bloco tem seu próprio componente puro.
 *   - Formulários POST-ão em /api/lp/:slug/submit com `values` + UTM
 *     (extraído da querystring) + referrer, cai em cms_lp_submissions e
 *     também em cms_leads pra pipeline de vendas.
 *   - HTML de blocos richtext / html custom é sanitizado (DOMPurify).
 */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function resolveSrc(u) {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  if (u.startsWith("/api/media/")) return `${process.env.REACT_APP_BACKEND_URL}${u}`;
  return u;
}

/** Parse a comma-separated hex color list from the CMS ("#FF5E00, #00B247").
 *  Returns undefined when empty so PlacesLogo falls back to the site defaults. */
function parseColorList(v) {
  if (!v) return undefined;
  if (Array.isArray(v)) return v.length ? v : undefined;
  const arr = String(v).split(",").map((s) => s.trim()).filter(Boolean);
  return arr.length ? arr : undefined;
}

/** Extrai UTMs da URL corrente para atrelar a cada submission. */
function collectUTM() {
  const q = new URLSearchParams(window.location.search);
  const out = {};
  for (const key of ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "gclid", "fbclid"]) {
    const v = q.get(key);
    if (v) out[key] = v;
  }
  return out;
}

const BG_CLASSES = {
  white: "bg-white text-black",
  neutral: "bg-neutral-100 text-black",
  black: "bg-black text-white",
  orange: "bg-accent-orange text-white",
  yellow: "bg-accent-yellow text-black",
};

export default function LandingPage() {
  const { slug } = useParams();
  const [lp, setLp] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ok | 404
  const utm = useMemo(() => (typeof window !== "undefined" ? collectUTM() : {}), []);

  useEffect(() => {
    axios.get(`${API}/lp/${slug}`)
      .then(({ data }) => { setLp(data); setStatus("ok"); })
      .catch(() => setStatus("404"));
  }, [slug]);

  if (status === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
      </div>
    );
  }
  if (status === "404" || !lp) return <NotFound />;

  const siteUrl = "https://agenciaplaces.com.br";
  const canonical = `${siteUrl}/lp/${lp.slug}`;
  const seoTitle = lp.meta_title || lp.title || "Places";
  const seoDesc = lp.meta_description || lp.description || "";
  const seoImage = lp.og_image || `${siteUrl}/og-default.jpg`;

  return (
    <div className="lp-root relative min-h-screen bg-white" data-testid={`lp-${lp.slug}`}>
      <Helmet prioritizeSeoTags>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDesc} />
        <link rel="canonical" href={canonical} />
        <meta name="robots" content={lp.noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large"} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDesc} />
        <meta property="og:image" content={seoImage} />
        <meta property="og:url" content={canonical} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDesc} />
        <meta name="twitter:image" content={seoImage} />
        {lp.favicon && <link rel="icon" href={resolveSrc(lp.favicon)} />}

        {/* Tracking */}
        {lp.ga_id && (
          <script async src={`https://www.googletagmanager.com/gtag/js?id=${lp.ga_id}`} />
        )}
        {lp.ga_id && (
          <script>{`
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date()); gtag('config', '${lp.ga_id}');
          `}</script>
        )}
        {lp.meta_pixel_id && (
          <script>{`
            !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');
            fbq('init', '${lp.meta_pixel_id}');fbq('track','PageView');
          `}</script>
        )}
        {lp.gtm_id && (
          <script>{`
            (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start': new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${lp.gtm_id}');
          `}</script>
        )}
      </Helmet>

      {/* Brand no topo-esquerdo, NO FLUXO da página (rola junto, não é fixa).
          Usa exatamente o mesmo container do conteúdo do hero
          (`max-w-5xl mx-auto px-6 md:px-12`) e é `absolute` dentro do
          `.lp-root` relativo — assim a origem horizontal é a largura do
          DOCUMENTO, não do viewport, e o alinhamento com o H1 do hero é
          pixel-perfect (com `fixed` a barra de rolagem deslocava ~8px). */}
      {lp.header?.enabled && (
        <div className="absolute top-0 inset-x-0 z-40 pointer-events-none" data-testid="lp-brand-sticky">
          <div className="max-w-5xl mx-auto px-6 md:px-12 pt-5 md:pt-7">
            <a href={lp.header.link || "/"} className="pointer-events-auto inline-block" aria-label="Places">
              <PlacesLogo
                colorMode={lp.header.brand_color === "black" ? "light" : "dark"}
                size="text-2xl md:text-3xl"
                speedMs={lp.header.logo_speed_ms}
                colors={parseColorList(lp.header.logo_colors)}
              />
            </a>
          </div>
        </div>
      )}

      {(lp.blocks || []).map((b, i) => (
        <Block key={b.id || i} block={b} slug={lp.slug} utm={utm}
          hideHeroBrand={!!lp.header?.enabled}
          logoSpeedMs={lp.header?.logo_speed_ms}
          logoColors={parseColorList(lp.header?.logo_colors)}
        />
      ))}

      {/* Sticky CTA (mobile) + WhatsApp floating (desktop after scroll) */}
      {lp.sticky_cta?.enabled && <StickyCTA config={lp.sticky_cta} />}
      {/* Bot Marina — mesmo widget da home, ligado por LP (config global do
          /admin/chatbot com override de número/mensagem/delay por LP). */}
      {lp.chatbot?.enabled && (
        <ChatBubble
          override={{ ...lp.chatbot, enabled: true }}
          offsetBottom={lp.sticky_cta?.enabled && lp.sticky_cta?.whatsapp_phone ? 84 : 0}
        />
      )}
      {/* Exit intent (desktop, after threshold, only once) */}
      {lp.exit_intent?.enabled && <ExitIntent config={lp.exit_intent} slug={lp.slug} utm={utm} />}
    </div>
  );
}

/* ============ Sticky CTA + WhatsApp float ============ */
function StickyCTA({ config }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const onScroll = () => {
      const pct = window.scrollY / (document.documentElement.scrollHeight - window.innerHeight) * 100;
      setVisible(pct >= (config.scroll_percent || 30));
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [config.scroll_percent]);
  if (!visible) return null;
  const wa = config.whatsapp_phone ? `https://wa.me/${config.whatsapp_phone.replace(/\D/g,"")}${config.whatsapp_message ? `?text=${encodeURIComponent(config.whatsapp_message)}` : ""}` : "";
  return (
    <>
      {/* Mobile: full-width bottom bar */}
      <div className="fixed bottom-0 inset-x-0 z-40 md:hidden bg-black text-white p-3 flex gap-2" data-testid="lp-sticky-mobile">
        <a href="#form-principal" onClick={()=>window.gtag?.("event","sticky_cta_click")} className="flex-1 text-center bg-accent-orange text-white font-semibold text-xs px-3 py-3 rounded-full">{config.label || "Falar sobre meu projeto"}</a>
        {wa && <a href={wa} target="_blank" rel="noopener noreferrer" onClick={()=>window.gtag?.("event","whatsapp_click")} className="bg-[#25D366] p-3 rounded-full"><MessageCircle className="w-5 h-5" /></a>}
      </div>
      {/* Desktop: floating WhatsApp bottom-right */}
      {wa && (
        <a href={wa} target="_blank" rel="noopener noreferrer" onClick={()=>window.gtag?.("event","whatsapp_click")}
          data-testid="lp-whatsapp-float"
          className="hidden md:flex fixed bottom-6 right-6 z-40 bg-[#25D366] hover:bg-[#128C7E] text-white p-4 rounded-full shadow-2xl items-center gap-2 transition-all">
          <MessageCircle className="w-6 h-6" />
        </a>
      )}
    </>
  );
}

function ExitIntent({ config, slug, utm }) {
  const [open, setOpen] = useState(false);
  const [shown, setShown] = useState(false);
  const [values, setValues] = useState({ name: "", phone: "" });
  const [sent, setSent] = useState(false);
  useEffect(() => {
    if (shown) return;
    const start = Date.now();
    let armed = false;
    const arm = () => {
      const elapsed = (Date.now() - start) / 1000;
      const pct = window.scrollY / (document.documentElement.scrollHeight - window.innerHeight) * 100;
      if (elapsed >= (config.delay_seconds || 45) || pct >= (config.scroll_percent || 40)) armed = true;
    };
    const onLeave = (e) => { if (armed && e.clientY < 10 && !shown) { setOpen(true); setShown(true); } };
    const t = setInterval(arm, 3000);
    document.addEventListener("mouseout", onLeave);
    return () => { clearInterval(t); document.removeEventListener("mouseout", onLeave); };
  }, [config, shown]);
  const submit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/lp/${slug}/submit`, { form_id: "exit-intent", values, utm, referrer: document.referrer });
      setSent(true);
      if (window.gtag) window.gtag("event","generate_lead",{ form_id: "exit-intent" });
    } catch {}
  };
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-6" role="dialog" aria-modal="true" data-testid="lp-exit-intent">
      <button className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={()=>setOpen(false)} aria-label="Fechar" />
      <div className="relative bg-white rounded-2xl p-8 max-w-lg w-full shadow-2xl">
        <button className="absolute top-3 right-3 p-2" onClick={()=>setOpen(false)} aria-label="Fechar"><X className="w-4 h-4" /></button>
        {sent ? (
          <div>
            <h2 className="font-display text-2xl md:text-3xl">Obrigado! Falaremos em breve.</h2>
            <p className="text-sm text-neutral-500 mt-2">A equipe da Places entrará em contato.</p>
          </div>
        ) : (
          <form onSubmit={submit}>
            <h2 className="font-display text-2xl md:text-3xl leading-tight">{config.title}</h2>
            <p className="text-sm text-neutral-500 mt-2 mb-6">{config.subtitle}</p>
            <input required placeholder="Nome" value={values.name} onChange={(e)=>setValues(s=>({...s,name:e.target.value}))} className="w-full border border-black/20 px-3 py-2.5 rounded mb-2 text-sm outline-none focus:border-black" />
            <input required placeholder="WhatsApp" value={values.phone} onChange={(e)=>setValues(s=>({...s,phone:e.target.value}))} className="w-full border border-black/20 px-3 py-2.5 rounded mb-4 text-sm outline-none focus:border-black" />
            <button type="submit" className="w-full bg-black text-white font-semibold text-sm px-6 py-3.5 rounded-full">{config.cta_label || "Quero conversar"}</button>
            <button type="button" onClick={()=>setOpen(false)} className="w-full text-xs text-neutral-400 mt-3 hover:text-black">Agora não</button>
          </form>
        )}
      </div>
    </div>
  );
}

/* ============ Per-block style (CMS) ============ */
/** Traduz os campos de estilo do bloco (definidos no CMS) em inline-style +
 *  classes-marcador. As cores viram CSS custom properties consumidas pelas
 *  regras `.lp-c-*` em index.css — então um bloco sem cor custom renderiza
 *  exatamente como antes. */
function blockWrap(block) {
  const style = {};
  const cls = [];
  const padTop = block.pad_top ?? block.spacing_px ?? 0;
  if (padTop) style.paddingTop = Number(padTop);
  if (block.pad_bottom) style.paddingBottom = Number(block.pad_bottom);
  if (block.min_height) { style.minHeight = Number(block.min_height); cls.push("lp-minh"); }
  if (block.bg_color) style.backgroundColor = block.bg_color;
  if (block.title_color) { style["--lp-title"] = block.title_color; cls.push("lp-c-title"); }
  if (block.body_color) { style["--lp-text"] = block.body_color; cls.push("lp-c-text"); }
  if (block.accent_color) { style["--lp-accent"] = block.accent_color; cls.push("lp-c-accent"); }
  if (block.btn_bg) {
    style["--lp-btn-bg"] = block.btn_bg;
    style["--lp-btn-text"] = block.btn_text || "#ffffff";
    cls.push("lp-c-btn");
  }
  return { style, cls: cls.join(" ") };
}

/* ============ Block dispatcher ============ */
function Block({ block, slug, utm, hideHeroBrand, logoSpeedMs, logoColors }) {
  const { style, cls } = blockWrap(block);
  const bgClass = block.bg_color ? "" : (BG_CLASSES[block.bg] || "");
  const wrap = (child) => (
    <div style={style} className={`lp-block ${bgClass} ${cls}`} data-lp-type={block.type}>{child}</div>
  );
  switch (block.type) {
    case "hero":        return wrap(<HeroBlock block={block} hideBrand={hideHeroBrand} logoSpeedMs={logoSpeedMs} logoColors={logoColors} />);
    case "richtext":    return wrap(<RichBlock block={block} />);
    case "image":       return wrap(<ImageBlock block={block} />);
    case "gallery":     return wrap(<GalleryBlock block={block} />);
    case "video":       return wrap(<VideoBlock block={block} />);
    case "features":    return wrap(<FeaturesBlock block={block} />);
    case "testimonial": return wrap(<TestimonialBlock block={block} />);
    case "form":        return wrap(<FormBlock block={block} slug={slug} utm={utm} />);
    case "cta":         return wrap(<CtaBlock block={block} />);
    case "spacer":      return <div style={{ height: block.height_px || 60, backgroundColor: block.bg_color || undefined }} className={block.bg_color ? "" : (BG_CLASSES[block.bg] || "bg-white")} />;
    case "divider":     return wrap(<DividerBlock block={block} />);
    case "html":        return wrap(<HtmlBlock block={block} />);
    case "stats":       return wrap(<StatsBlock block={block} />);
    case "multi_step_form": return wrap(<MultiStepFormBlock block={block} slug={slug} utm={utm} />);
    case "logos_band":  return wrap(<LogosBandBlock block={block} />);
    case "faq":         return wrap(<FaqBlock block={block} />);
    case "process_timeline": return wrap(<ProcessTimelineBlock block={block} />);
    case "services_grouped": return wrap(<ServicesGroupedBlock block={block} />);
    case "case_showcase":    return wrap(<CaseShowcaseBlock block={block} />);
    default: return null;
  }
}

/* ============ Selo Google Partner (código, trocável por imagem no CMS) ============ */
function GoogleGlyph({ className = "w-4 h-4" }) {
  return (
    <svg viewBox="0 0 48 48" className={className} aria-hidden>
      <path fill="#4285F4" d="M45.1 24.5c0-1.6-.1-2.8-.4-4H24v7.6h12c-.2 2-1.5 5-4.4 7l6.7 5.2c4-3.7 6.8-9.1 6.8-15.8z" />
      <path fill="#34A853" d="M24 46c6 0 11-2 14.3-5.7l-6.7-5.2c-1.8 1.3-4.3 2.2-7.6 2.2-6 0-11-4-12.8-9.5l-7 5.4C7.6 41 15.2 46 24 46z" />
      <path fill="#FBBC05" d="M11.2 27.8c-.5-1.4-.8-2.9-.8-4.4s.3-3 .7-4.4l-7-5.4C2.8 16.5 2 20.1 2 23.4s.8 6.9 2.2 9.8l7-5.4z" />
      <path fill="#EA4335" d="M24 9.5c4.2 0 7.1 1.8 8.7 3.3l6.3-6.1C35 3.1 30 1 24 1 15.2 1 7.6 6 4.2 13.6l7 5.4C13 13.5 18 9.5 24 9.5z" />
    </svg>
  );
}

function GooglePartnerBadge({ image, label }) {
  if (image) {
    return (
      <div className="mb-8" data-testid="lp-hero-google-partner">
        <img src={resolveSrc(image)} alt={label || "Google Partner"} className="h-12 md:h-14 w-auto" />
      </div>
    );
  }
  return (
    <div className="mb-8 inline-flex items-center gap-2.5 bg-white text-black rounded-full pl-2 pr-4 py-1.5 shadow-lg shadow-black/20"
      data-testid="lp-hero-google-partner">
      <span className="w-7 h-7 rounded-full bg-white border border-black/10 grid place-items-center">
        <GoogleGlyph className="w-4 h-4" />
      </span>
      <span className="text-[10px] md:text-[11px] font-semibold tracking-[0.18em] uppercase">{label || "Google Partner"}</span>
    </div>
  );
}

/* ============ Blocks ============ */
function HeroBlock({ block, hideBrand, logoSpeedMs, logoColors }) {
  const isDark = (block.text_color || "white") === "white";
  const overlay = block.overlay_opacity ?? 0.4;
  return (
    <section
      className="relative w-full flex items-center overflow-hidden"
      style={{ minHeight: block.height || "70vh" }}
      data-testid="lp-hero"
    >
      {block.video_url ? (
        <video autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover">
          <source src={resolveSrc(block.video_url)} type="video/mp4" />
        </video>
      ) : block.image ? (
        <img src={resolveSrc(block.image)} alt="" className="absolute inset-0 w-full h-full object-cover" />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-accent-orange to-accent-yellow" />
      )}
      <div className="absolute inset-0" style={{ backgroundColor: `rgba(0,0,0,${overlay})` }} />
      {/* `w-full` é obrigatório: a <section> é flex, então sem ele este
          container encolhe para o max-content (992px) em vez de ocupar os
          1024px do max-w-5xl — o que desalinhava o conteúdo do hero em 16px
          em relação à marca do topo. */}
      <div className={`relative z-10 w-full max-w-5xl mx-auto px-6 md:px-12 py-24 md:py-32 ${isDark ? "text-white" : "text-black"}`}>
        {/* Per-hero brand (top-LEFT, alinhado com o conteúdo do hero).
            `block.show_brand` toggles it, `block.brand_color` = "white" | "black".
            Skipped when a global sticky header brand is enabled (avoids duplication). */}
        {block.show_brand && !hideBrand && (
          <div className="mb-10 md:mb-14" data-testid="lp-hero-brand">
            <PlacesLogo
              colorMode={block.brand_color === "black" ? "light" : "dark"}
              size="text-2xl md:text-3xl"
              speedMs={logoSpeedMs}
              colors={logoColors}
            />
          </div>
        )}
        {block.show_google_partner && (
          <GooglePartnerBadge image={block.google_partner_image} label={block.google_partner_label} />
        )}
        {block.eyebrow && <div className="text-[10px] md:text-xs uppercase tracking-[0.3em] font-semibold text-accent-yellow mb-6">{block.eyebrow}</div>}
        {block.title && <h1 className="font-display text-5xl md:text-7xl leading-[0.95] max-w-4xl">{block.title}</h1>}
        {block.subtitle && <p className="mt-6 text-lg md:text-xl max-w-2xl opacity-90">{block.subtitle}</p>}
        <div className="flex flex-wrap items-center gap-4 mt-8">
          {block.cta_label && (
            <a href={block.cta_link || "#form"} onClick={()=>window.gtag?.("event","hero_cta_click")} data-testid="lp-hero-cta"
              className="inline-flex items-center gap-2 bg-accent-yellow text-black font-semibold text-sm px-8 py-4 rounded-full hover:bg-white transition-colors">
              {block.cta_label} <ArrowRight className="w-4 h-4" />
            </a>
          )}
          {block.cta_secondary_label && (
            <a href={block.cta_secondary_link || "#"} className="text-sm font-semibold underline underline-offset-4 decoration-accent-yellow decoration-2 hover:decoration-white">
              {block.cta_secondary_label}
            </a>
          )}
        </div>
        {block.microproof && <p className="mt-10 text-xs md:text-sm opacity-70 max-w-3xl">{block.microproof}</p>}
      </div>
    </section>
  );
}

function RichBlock({ block }) {
  const width = block.max_width === "prose" ? "max-w-3xl" : block.max_width === "wide" ? "max-w-5xl" : "max-w-none";
  const align = block.align === "center" ? "text-center mx-auto" : "text-left";
  const html = useMemo(() => DOMPurify.sanitize(block.html || ""), [block.html]);
  return (
    <div className={`px-6 md:px-12 py-12 ${width} ${align} mx-auto`}>
      <div className="lp-rich prose prose-lg max-w-none" dangerouslySetInnerHTML={{ __html: html }} />
    </div>
  );
}

function ImageBlock({ block }) {
  const width = block.max_width === "prose" ? "max-w-3xl" : block.max_width === "wide" ? "max-w-6xl" : "max-w-none";
  return (
    <figure className={`px-6 md:px-12 py-8 ${width} mx-auto`}>
      {block.src && <img src={resolveSrc(block.src)} alt={block.alt || ""} loading="lazy" className="w-full h-auto rounded" />}
      {block.caption && <figcaption className="text-xs text-center text-neutral-500 mt-2">{block.caption}</figcaption>}
    </figure>
  );
}

function GalleryBlock({ block }) {
  const cols = block.columns || 3;
  const gridCols = `grid-cols-2 md:grid-cols-${cols}`;
  return (
    <div className="px-6 md:px-12 py-12 max-w-6xl mx-auto">
      <div className={`grid ${gridCols} gap-3`}>
        {(block.images || []).map((img, i) => (
          <figure key={i}>
            {img.src && <img src={resolveSrc(img.src)} alt={img.alt || ""} loading="lazy" className="w-full aspect-square object-cover rounded" />}
            {img.caption && <figcaption className="text-[10px] text-neutral-500 mt-1">{img.caption}</figcaption>}
          </figure>
        ))}
      </div>
    </div>
  );
}

function VideoBlock({ block }) {
  const url = block.url || "";
  const yt = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|v\/))([^?&\s]+)/i);
  const vimeo = url.match(/vimeo\.com\/(\d+)/i);
  return (
    <div className="px-6 md:px-12 py-12 max-w-5xl mx-auto">
      <div className="aspect-video overflow-hidden rounded bg-black">
        {yt ? (
          <iframe src={`https://www.youtube.com/embed/${yt[1]}`} className="w-full h-full" allowFullScreen title={block.caption || "Video"} />
        ) : vimeo ? (
          <iframe src={`https://player.vimeo.com/video/${vimeo[1]}`} className="w-full h-full" allowFullScreen title={block.caption || "Video"} />
        ) : url ? (
          <video src={url} controls className="w-full h-full" />
        ) : (
          <div className="flex items-center justify-center text-white/70">Configure a URL do vídeo.</div>
        )}
      </div>
      {block.caption && <p className="text-xs text-center text-neutral-500 mt-2">{block.caption}</p>}
    </div>
  );
}

function FeaturesBlock({ block }) {
  const cols = block.columns || 3;
  const gridCols = `md:grid-cols-${cols}`;
  const isPillars = block.variant === "pillars";
  return (
    <div className="px-6 md:px-12 py-16 max-w-6xl mx-auto">
      {(block.eyebrow || block.title || block.subtitle) && (
        <div className={`mb-10 ${isPillars ? "" : ""}`}>
          {block.eyebrow && <div className="text-[10px] uppercase tracking-[0.3em] font-semibold text-accent-orange mb-2">{block.eyebrow}</div>}
          {block.title && <h2 className="font-display text-3xl md:text-5xl leading-tight whitespace-pre-line">{block.title}</h2>}
          {block.subtitle && <p className="text-neutral-500 mt-3 max-w-3xl">{block.subtitle}</p>}
        </div>
      )}
      <div className={`grid grid-cols-1 ${gridCols} gap-8`}>
        {(block.items || []).map((it, i) => {
          const isNum = /^\d+$/.test(String(it.icon || ""));
          const IconCmp = !isNum ? (Icons[toPascal(it.icon || "star")] || Icons.Star) : null;
          return (
            <div key={i}>
              {isNum ? (
                <div className="font-display text-4xl mb-3 text-accent-orange">0{it.icon}</div>
              ) : (
                <IconCmp className="w-8 h-8 mb-3 text-accent-orange" />
              )}
              {it.title && <h3 className="font-display text-xl mb-1">{it.title}</h3>}
              {it.description && <p className="text-sm text-neutral-600 leading-relaxed">{it.description}</p>}
            </div>
          );
        })}
      </div>
      {block.footer && <p className="mt-12 font-display text-xl md:text-2xl italic max-w-3xl">{block.footer}</p>}
      {block.cta_label && <div className="mt-8"><a href={block.cta_link||"#form"} className="inline-flex items-center gap-2 bg-black text-white font-semibold text-sm px-8 py-4 rounded-full hover:bg-accent-orange">{block.cta_label}<ArrowRight className="w-4 h-4" /></a></div>}
    </div>
  );
}

function toPascal(s) { return String(s).replace(/(^|-|_)(\w)/g, (_, __, c) => c.toUpperCase()); }

function TestimonialBlock({ block }) {
  return (
    <div className="px-6 md:px-12 py-16 max-w-5xl mx-auto space-y-10">
      {(block.items || []).map((it, i) => (
        <blockquote key={i} className="border-l-4 border-accent-orange pl-6">
          <p className="text-xl md:text-2xl font-display italic leading-relaxed">"{it.quote}"</p>
          <footer className="mt-4 flex items-center gap-3">
            {it.avatar && <img src={resolveSrc(it.avatar)} alt="" className="w-10 h-10 rounded-full object-cover" />}
            <div className="text-sm">
              <div className="font-semibold">{it.author}</div>
              {it.role && <div className="text-xs text-neutral-500">{it.role}</div>}
            </div>
          </footer>
        </blockquote>
      ))}
    </div>
  );
}

function FormBlock({ block, slug, utm }) {
  const [values, setValues] = useState({});
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const set = (name, v) => setValues((s) => ({ ...s, [name]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true); setError("");
    try {
      await axios.post(`${API}/lp/${slug}/submit`, {
        form_id: block.form_id || "default",
        values,
        utm,
        referrer: document.referrer,
      });
      setSent(true);
      // Fire tracking events if available
      if (window.gtag) window.gtag("event", "generate_lead", { form_id: block.form_id });
      if (window.fbq) window.fbq("track", "Lead");
    } catch (err) {
      setError(err?.response?.data?.detail || "Erro ao enviar. Tente novamente.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div id={block.form_id || "form"} className="px-6 md:px-12 py-16 max-w-2xl mx-auto scroll-mt-24" data-testid={`lp-form-${block.form_id || "default"}`}>
      {(block.title || block.subtitle) && (
        <div className="text-center mb-8">
          {block.title && <h2 className="font-display text-3xl md:text-5xl">{block.title}</h2>}
          {block.subtitle && <p className="text-neutral-500 mt-3">{block.subtitle}</p>}
        </div>
      )}
      {sent ? (
        <div className="text-center py-12 border border-green-200 bg-green-50 rounded-md">
          <div className="text-3xl mb-2">✓</div>
          <p className="text-lg font-semibold">{block.thank_you || "Obrigado! Retornaremos em breve."}</p>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          {(block.fields || []).map((f, i) => (
            <div key={i}>
              <label className="block text-xs font-semibold uppercase tracking-widest text-neutral-600 mb-1">
                {f.label}{f.required && <span className="text-red-500 ml-0.5">*</span>}
              </label>
              {f.type === "textarea" ? (
                <textarea rows={4} required={f.required} placeholder={f.placeholder}
                  value={values[f.name] || ""} onChange={(e) => set(f.name, e.target.value)}
                  className="w-full border border-black/20 px-3 py-2.5 rounded focus:border-black outline-none" />
              ) : f.type === "select" ? (
                <select required={f.required} value={values[f.name] || ""} onChange={(e) => set(f.name, e.target.value)}
                  className="w-full border border-black/20 px-3 py-2.5 rounded focus:border-black outline-none">
                  <option value="">{f.placeholder || "Selecione…"}</option>
                  {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : f.type === "checkbox" ? (
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={!!values[f.name]} onChange={(e) => set(f.name, e.target.checked)} required={f.required} />
                  {f.placeholder || f.label}
                </label>
              ) : (
                <input type={f.type || "text"} required={f.required} placeholder={f.placeholder}
                  value={values[f.name] || ""} onChange={(e) => set(f.name, e.target.value)}
                  className="w-full border border-black/20 px-3 py-2.5 rounded focus:border-black outline-none" />
              )}
            </div>
          ))}
          {error && <div className="text-sm text-red-500">{error}</div>}
          <button type="submit" disabled={sending} data-testid="lp-form-submit"
            className="w-full bg-black text-white font-semibold text-sm px-6 py-4 rounded-full hover:bg-accent-orange disabled:opacity-50 flex items-center justify-center gap-2">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {sending ? "Enviando…" : (block.submit_label || "Enviar")}
          </button>
        </form>
      )}
    </div>
  );
}

function CtaBlock({ block }) {
  return (
    <div className="px-6 md:px-12 py-16 max-w-4xl mx-auto text-center">
      {block.title && <h2 className="font-display text-3xl md:text-5xl">{block.title}</h2>}
      {block.subtitle && <p className="text-neutral-500 mt-3 mb-8 max-w-2xl mx-auto">{block.subtitle}</p>}
      <div className="flex flex-wrap justify-center gap-3 mt-6">
        {(block.buttons || []).map((b, i) => {
          const url = b.is_whatsapp
            ? `https://wa.me/${(b.whatsapp_phone || "").replace(/\D/g, "")}${b.whatsapp_message ? `?text=${encodeURIComponent(b.whatsapp_message)}` : ""}`
            : b.link || "#";
          const styles = {
            primary: "bg-black text-white hover:bg-accent-orange",
            yellow: "bg-accent-yellow text-black hover:bg-black hover:text-white",
            orange: "bg-accent-orange text-white hover:bg-black",
            whatsapp: "bg-[#25D366] text-white hover:bg-[#128C7E]",
            outline: "border-2 border-black text-black hover:bg-black hover:text-white",
          };
          return (
            <a key={i} href={url} target={b.is_whatsapp || (b.link || "").startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer"
              data-testid={`lp-cta-btn-${i}`}
              className={`inline-flex items-center gap-2 font-semibold text-sm px-8 py-4 rounded-full transition-colors ${styles[b.style] || styles.primary}`}>
              {b.is_whatsapp && <MessageCircle className="w-4 h-4" />}
              {b.label}
            </a>
          );
        })}
      </div>
    </div>
  );
}

function DividerBlock({ block }) {
  const style = block.style === "dashed" ? "border-dashed" : block.style === "thick" ? "border-t-4" : "border-t";
  return (
    <div className="px-6 md:px-12 max-w-5xl mx-auto py-4">
      <hr className={`${style}`} style={{ borderColor: block.color || "rgba(0,0,0,0.12)" }} />
    </div>
  );
}

function HtmlBlock({ block }) {
  const html = useMemo(() => DOMPurify.sanitize(block.html || "", { ADD_TAGS: ["iframe"], ADD_ATTR: ["allow", "allowfullscreen", "frameborder", "scrolling"] }), [block.html]);
  return <div className="px-6 md:px-12 py-8 max-w-5xl mx-auto" dangerouslySetInnerHTML={{ __html: html }} />;
}

function StatsBlock({ block }) {
  const cols = block.columns || 3;
  return (
    <div className="px-6 md:px-12 py-16 max-w-6xl mx-auto">
      {block.eyebrow && <div className="text-[10px] uppercase tracking-[0.3em] font-semibold text-accent-orange mb-2">{block.eyebrow}</div>}
      {block.title && <h2 className="font-display text-3xl md:text-5xl leading-tight whitespace-pre-line mb-4">{block.title}</h2>}
      {block.subtitle && <p className="text-neutral-500 mt-2 mb-8 max-w-3xl">{block.subtitle}</p>}
      <div className={`grid grid-cols-2 md:grid-cols-${cols} gap-6`}>
        {(block.items || []).map((s, i) => (
          <div key={i}>
            <div className="font-display text-4xl md:text-6xl leading-none">
              {s.value}<span className="text-2xl align-top ml-1">{s.suffix}</span>
            </div>
            <div className="text-xs uppercase tracking-widest text-neutral-500 mt-2">{s.label}</div>
          </div>
        ))}
      </div>
      {block.footer && <p className="mt-10 font-display text-xl md:text-2xl italic">{block.footer}</p>}
    </div>
  );
}

/* ============ NEW: MultiStepForm ============ */
function MultiStepFormBlock({ block, slug, utm }) {
  const [step, setStep] = useState(0);
  const [values, setValues] = useState({});
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [lgpd, setLgpd] = useState(false);
  const set = (n, v) => setValues((s) => ({ ...s, [n]: v }));
  const steps = block.steps || [];
  const isLast = step === steps.length - 1;

  const next = (e) => {
    e.preventDefault();
    if (window.gtag) window.gtag("event", `form_step_${step + 1}_complete`);
    setStep(step + 1);
  };
  const submit = async (e) => {
    e.preventDefault();
    if (!lgpd && block.lgpd_text) { setError("Você precisa aceitar a política de privacidade."); return; }
    setSending(true); setError("");
    try {
      await axios.post(`${API}/lp/${slug}/submit`, { form_id: block.form_id, values, utm, referrer: document.referrer });
      setSent(true);
      if (window.gtag) window.gtag("event", "generate_lead", { form_id: block.form_id });
      if (window.fbq) window.fbq("track", "Lead");
    } catch (err) { setError(err?.response?.data?.detail || "Erro. Tente novamente."); }
    finally { setSending(false); }
  };
  const wa = block.whatsapp_phone ? `https://wa.me/${block.whatsapp_phone.replace(/\D/g,"")}${block.whatsapp_message ? `?text=${encodeURIComponent(block.whatsapp_message)}` : ""}` : "";

  return (
    <div id={block.form_id || "form"} className="px-6 md:px-12 py-16 max-w-5xl mx-auto scroll-mt-24" data-testid={`lp-multiform-${block.form_id}`}>
      <div className="grid md:grid-cols-2 gap-12 items-start">
        <div>
          {block.eyebrow && <div className="text-[10px] uppercase tracking-[0.3em] font-semibold text-accent-orange mb-2">{block.eyebrow}</div>}
          <h2 className="font-display text-3xl md:text-5xl leading-tight">{block.title}</h2>
          {block.subtitle && <p className="text-neutral-600 mt-4">{block.subtitle}</p>}
          {block.microcopy && <p className="text-xs text-neutral-400 mt-6 italic">{block.microcopy}</p>}
          {wa && (
            <a href={wa} target="_blank" rel="noopener noreferrer" onClick={()=>window.gtag?.("event","whatsapp_click")}
              className="inline-flex items-center gap-2 mt-6 bg-[#25D366] text-white font-semibold text-sm px-6 py-3 rounded-full hover:bg-[#128C7E]">
              <MessageCircle className="w-4 h-4" /> {block.whatsapp_label || "Falar no WhatsApp"}
            </a>
          )}
        </div>
        <div className="bg-neutral-50 border border-black/10 p-6 md:p-8 rounded-lg">
          {sent ? (
            <div data-testid="lp-multiform-success">
              <Check className="w-8 h-8 text-green-600 mb-3" />
              <h3 className="font-display text-2xl leading-tight">{block.thank_you_title || "Recebemos seu projeto."}</h3>
              <p className="text-sm text-neutral-600 mt-3">{block.thank_you_body}</p>
              {wa && <a href={wa} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 mt-6 bg-[#25D366] text-white font-semibold text-sm px-6 py-3 rounded-full"><MessageCircle className="w-4 h-4" /> Falar agora pelo WhatsApp</a>}
            </div>
          ) : (
            <form onSubmit={isLast ? submit : next}>
              {/* Progress */}
              <div className="flex items-center gap-2 mb-6">
                {steps.map((_, i) => <div key={i} className={`h-1 flex-1 rounded ${i <= step ? "bg-accent-orange" : "bg-black/10"}`} />)}
                <span className="text-[10px] font-mono text-neutral-500 ml-2">{step + 1}/{steps.length}</span>
              </div>
              {steps[step]?.title && <h3 className="font-display text-xl mb-4">{steps[step].title}</h3>}
              <div className="space-y-3">
                {(steps[step]?.fields || []).map((f, i) => (
                  <div key={i}>
                    <label className="block text-[10px] uppercase tracking-widest font-semibold text-neutral-600 mb-1">{f.label}{f.required && <span className="text-red-500 ml-0.5">*</span>}</label>
                    {f.type === "textarea" ? (
                      <textarea rows={3} required={f.required} value={values[f.name]||""} onChange={(e)=>set(f.name,e.target.value)} className="w-full border border-black/20 px-3 py-2.5 rounded outline-none focus:border-black text-sm" />
                    ) : f.type === "select" ? (
                      <select required={f.required} value={values[f.name]||""} onChange={(e)=>set(f.name,e.target.value)} className="w-full border border-black/20 px-3 py-2.5 rounded outline-none focus:border-black text-sm">
                        <option value="">Selecione…</option>
                        {(f.options||[]).map((o)=><option key={o} value={o}>{o}</option>)}
                      </select>
                    ) : (
                      <input type={f.type||"text"} required={f.required} value={values[f.name]||""} onChange={(e)=>set(f.name,e.target.value)} className="w-full border border-black/20 px-3 py-2.5 rounded outline-none focus:border-black text-sm" />
                    )}
                  </div>
                ))}
              </div>
              {isLast && block.lgpd_text && (
                <label className="flex items-start gap-2 mt-4 text-xs text-neutral-600">
                  <input type="checkbox" checked={lgpd} onChange={(e)=>setLgpd(e.target.checked)} className="mt-0.5" />
                  <span>{block.lgpd_text}</span>
                </label>
              )}
              {error && <div className="mt-3 text-sm text-red-500">{error}</div>}
              <button type="submit" disabled={sending} data-testid="lp-multiform-submit" className="mt-6 w-full bg-black text-white font-semibold text-sm px-6 py-4 rounded-full hover:bg-accent-orange disabled:opacity-50 flex items-center justify-center gap-2">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                {sending ? "Enviando…" : isLast ? (block.submit_label || "Enviar") : (steps[step]?.next_label || "Continuar")}
              </button>
              {step > 0 && <button type="button" onClick={()=>setStep(step-1)} className="w-full text-xs text-neutral-500 mt-2 hover:text-black">← Voltar</button>}
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

/* ============ NEW: LogosBand ============ */
/** Importa as marcas de clientes do site (mesma fonte da vitrine da home)
 *  a partir dos nomes selecionados no CMS. `block.logos` (manual) segue como
 *  fallback. Tamanho do logo configurável por bloco (`logo_scale`). */
function LogosBandBlock({ block }) {
  const { clients: ALL_CLIENTS = [] } = useContent();
  const logos = useMemo(() => {
    const picked = block.client_names || [];
    if (block.use_site_clients && !picked.length) {
      return (ALL_CLIENTS || []).map((c) => ({ name: c.name, src: c.logo }));
    }
    if (picked.length) {
      return picked
        .map((n) => (ALL_CLIENTS || []).find((c) => c.name === n))
        .filter(Boolean)
        .map((c) => ({ name: c.name, src: c.logo }));
    }
    return block.logos || [];
  }, [block.client_names, block.use_site_clients, block.logos, ALL_CLIENTS]);

  const cols = block.columns || 6;
  const scale = Math.min(95, Math.max(30, Number(block.logo_scale || 72)));
  const dark = !!block.bg_color && isDarkColor(block.bg_color);

  return (
    <div className="px-6 md:px-12 py-16 max-w-6xl mx-auto">
      {block.title && <h2 className="font-display text-2xl md:text-3xl">{block.title}</h2>}
      {block.subtitle && <p className="text-sm text-neutral-500 mt-2 mb-8">{block.subtitle}</p>}
      <div className="grid gap-4 mt-8 grid-cols-2 md:[grid-template-columns:var(--lp-logo-cols)]"
        style={{ "--lp-logo-cols": `repeat(${cols}, minmax(0,1fr))` }}>
        {logos.map((l, i) => (
          <div key={i} className={`aspect-[3/2] flex items-center justify-center p-3 border ${dark ? "border-white/10" : "border-black/10"}`}
            data-testid={`lp-logo-${i}`}>
            {l.src ? (
              <img src={resolveSrc(l.src)} alt={l.name}
                style={{ height: `${scale}%`, width: `${Math.min(92, scale + 22)}%` }}
                className={`object-contain transition-all ${dark ? "filter brightness-0 invert opacity-80 hover:opacity-100" : "grayscale hover:grayscale-0"}`} />
            ) : (
              <span className="font-display font-semibold text-sm md:text-lg leading-tight text-center opacity-70">{l.name}</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/** Luminância aproximada — decide se o fundo custom da dobra é escuro
 *  (aí os logos entram em branco) ou claro (grayscale). */
function isDarkColor(hex) {
  const m = String(hex).replace("#", "");
  if (m.length < 6) return false;
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(m.slice(i, i + 2), 16));
  return (0.299 * r + 0.587 * g + 0.114 * b) < 140;
}

/* ============ NEW: FAQ accordion ============ */
function FaqBlock({ block }) {
  const [openIdx, setOpen] = useState(-1);
  return (
    <div className="px-6 md:px-12 py-16 max-w-3xl mx-auto">
      {block.title && <h2 className="font-display text-3xl md:text-5xl text-center mb-10">{block.title}</h2>}
      <div className="divide-y divide-black/10 border-y border-black/10">
        {(block.items || []).map((it, i) => (
          <div key={i}>
            <button onClick={()=>{ setOpen(openIdx===i?-1:i); window.gtag?.("event","faq_open",{q:it.q}); }}
              className="w-full text-left py-5 flex justify-between items-center gap-4 hover:text-accent-orange">
              <span className="font-semibold text-base md:text-lg">{it.q}</span>
              <ChevronDown className={`w-5 h-5 shrink-0 transition-transform ${openIdx===i?"rotate-180":""}`} />
            </button>
            {openIdx===i && <p className="pb-5 text-neutral-600 text-sm md:text-base leading-relaxed">{it.a}</p>}
          </div>
        ))}
      </div>
      {block.cta_label && <div className="text-center mt-10"><a href={block.cta_link||"#form"} className="inline-flex items-center gap-2 bg-black text-white font-semibold text-sm px-8 py-4 rounded-full hover:bg-accent-orange">{block.cta_label}<ArrowRight className="w-4 h-4" /></a></div>}
    </div>
  );
}

/* ============ NEW: ProcessTimeline (horizontal steps with hover) ============ */
function ProcessTimelineBlock({ block }) {
  const [active, setActive] = useState(0);
  return (
    <div className="px-6 md:px-12 py-16 max-w-6xl mx-auto">
      {block.title && <h2 className="font-display text-3xl md:text-5xl leading-tight">{block.title}</h2>}
      {block.subtitle && <p className="text-neutral-600 mt-4 max-w-3xl">{block.subtitle}</p>}
      <div className="mt-12">
        <div className="flex overflow-x-auto md:overflow-visible gap-1 md:gap-0 items-stretch">
          {(block.steps || []).map((s, i) => (
            <button key={i} onClick={()=>setActive(i)} onMouseEnter={()=>setActive(i)}
              className={`flex-1 min-w-[140px] md:min-w-0 text-left border-t-4 pt-3 transition-all ${active===i?"border-accent-orange":"border-black/10 hover:border-black/40"}`}>
              <div className="text-[10px] font-mono text-neutral-400">0{i+1}</div>
              <div className={`text-xs md:text-sm font-semibold mt-1 uppercase tracking-wider ${active===i?"text-black":"text-neutral-500"}`}>{s.label}</div>
            </button>
          ))}
        </div>
        <div className="mt-8 bg-neutral-50 border border-black/10 rounded p-6 md:p-8 min-h-[100px]">
          <p className="text-base md:text-lg leading-relaxed">{block.steps?.[active]?.description}</p>
        </div>
      </div>
      {block.footer && <p className="mt-8 font-display text-xl md:text-2xl italic text-center">{block.footer}</p>}
    </div>
  );
}

/* ============ NEW: ServicesGrouped ============ */
function ServicesGroupedBlock({ block }) {
  return (
    <div className="px-6 md:px-12 py-16 max-w-6xl mx-auto">
      {block.title && <h2 className="font-display text-3xl md:text-5xl leading-tight max-w-4xl">{block.title}</h2>}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mt-12">
        {(block.groups || []).map((g, i) => (
          <div key={i} className="border-t-2 border-accent-orange pt-4">
            <h3 className="text-xs uppercase tracking-widest font-bold text-black mb-3">{g.title}</h3>
            <ul className="space-y-1.5">
              {(g.items || []).map((it, j) => <li key={j} className="text-sm text-neutral-700">{it}</li>)}
            </ul>
          </div>
        ))}
      </div>
      {block.footer && <p className="mt-12 font-display text-xl md:text-2xl italic">{block.footer}</p>}
      {block.cta_label && <div className="mt-8"><a href={block.cta_link||"#form"} className="inline-flex items-center gap-2 bg-black text-white font-semibold text-sm px-8 py-4 rounded-full hover:bg-accent-orange">{block.cta_label}<ArrowRight className="w-4 h-4" /></a></div>}
    </div>
  );
}

/* ============ NEW: CaseShowcase ============ */
/** Puxa os cases reais do site (mesma fonte da página /cases) a partir dos
 *  slugs escolhidos no CMS — thumb, cliente, nome, categorias e link vêm
 *  automaticamente, sem redigitação. `block.cases` (manual) segue como
 *  fallback para LPs antigas. */
function stripHtml(v, max = 200) {
  const txt = String(v || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  return txt.length > max ? `${txt.slice(0, max).trim()}…` : txt;
}

function CaseShowcaseBlock({ block }) {
  const { cases: ALL_CASES = [] } = useContent();
  const cases = useMemo(() => {
    const slugs = block.case_slugs || [];
    if (!slugs.length) return block.cases || [];
    return slugs
      .map((s) => (ALL_CASES || []).find((c) => c.slug === s))
      .filter(Boolean)
      .map((c) => ({
        name: c.title,
        client: [c.developer, [c.city, c.state].filter(Boolean).join("/")].filter(Boolean).join(" · "),
        categories: Array.isArray(c.categories) ? c.categories.join(" · ") : (c.categories || ""),
        image: c.cover,
        link: `/pt-br/cases/${c.slug}`,
        challenge: stripHtml(c.situation),
        response: stripHtml(c.solution),
      }));
  }, [block.case_slugs, ALL_CASES, block.cases]);
  return (
    <div id="cases" className="px-6 md:px-12 py-16 max-w-6xl mx-auto">
      {block.eyebrow && <div className="text-[10px] uppercase tracking-[0.3em] font-semibold text-accent-orange mb-2">{block.eyebrow}</div>}
      {block.title && <h2 className="font-display text-3xl md:text-5xl leading-tight max-w-4xl mb-12">{block.title}</h2>}
      <div className="grid md:grid-cols-2 gap-8 md:gap-10">
        {cases.map((c, i) => (
          <div key={i} className="border border-black/10 rounded-lg overflow-hidden hover:shadow-xl transition-shadow group">
            <div className="aspect-video bg-neutral-200 overflow-hidden">
              {c.image ? <img src={resolveSrc(c.image)} alt={c.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /> : <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-accent-orange/20 to-accent-yellow/20 text-2xl font-display">{c.name}</div>}
            </div>
            <div className="p-6">
              <div className="text-[10px] uppercase tracking-widest font-semibold text-neutral-500">{c.client}</div>
              <h3 className="font-display text-2xl md:text-3xl mt-1">{c.name}</h3>
              <div className="text-[10px] uppercase tracking-widest text-accent-orange mt-2 font-semibold">{c.categories}</div>
              <div className="mt-4 space-y-3 text-sm">
                <div><div className="text-[10px] uppercase font-bold text-neutral-500">Desafio</div><p className="text-neutral-700 mt-1">{c.challenge}</p></div>
                <div><div className="text-[10px] uppercase font-bold text-neutral-500">Resposta Places</div><p className="text-neutral-700 mt-1">{c.response}</p></div>
              </div>
              <div className="flex gap-2 mt-5">
                {c.link && <a href={c.link} className="text-xs bg-black text-white font-semibold px-4 py-2 rounded-full hover:bg-accent-orange">{block.cta_label || "Ver case"} →</a>}
                <a href={block.cta_secondary_link || "#form-principal"} className="text-xs border border-black text-black font-semibold px-4 py-2 rounded-full hover:bg-black hover:text-white">{block.cta_secondary_label || "Quero um projeto assim"}</a>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
